import React, { useEffect, useRef, useState } from "react";
import {
  ArrowRight,
  BadgeCheck,
  BriefcaseBusiness,
  Camera,
  Check,
  Copy,
  Edit3,
  Heart,
  Home,
  LocateFixed,
  LogOut,
  MapPin,
  Mail,
  PackageCheck,
  Phone,
  Plus,
  ShieldCheck,
  Share2,
  Star,
  Trash2,
  UserRound,
} from "lucide-react";
import { Link, useOutletContext } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useToast } from "../contexts/ToastContext";
import { getErrorMessage, getProfileImage } from "../core/api.jsx";
import {
  addCustomerAddress,
  deleteCustomerAddress,
  getCustomerAddresses,
  setDefaultCustomerAddress,
  updateCustomerAddress,
  updateCustomerProfile,
  uploadCustomerProfileImage,
} from "../core/services/customerService";

const emptyAddress = (user) => ({
  fullName: user?.name || "",
  phoneNo: user?.phone || "",
  address: "",
  city: "",
  state: "",
  country: "India",
  pinCode: "",
  landmark: "",
  label: "home",
  isDefault: false,
});

export default function Profile() {
  const { onLogin } = useOutletContext();
  const { isAuthenticated, user, token, setSession, clearSession } = useAuth();
  const { notify } = useToast();
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isUploadingPhoto, setIsUploadingPhoto] = useState(false);
  const [form, setForm] = useState({ name: user?.name || "", email: user?.email || "", gender: user?.gender || "" });
  const [addresses, setAddresses] = useState([]);
  const [addressesLoading, setAddressesLoading] = useState(true);
  const [addressFormOpen, setAddressFormOpen] = useState(false);
  const [addressEditingId, setAddressEditingId] = useState("");
  const [addressBusy, setAddressBusy] = useState(false);
  const [locationLoading, setLocationLoading] = useState(false);
  const [addressForm, setAddressForm] = useState(() => emptyAddress(user));
  const photoInputRef = useRef(null);

  useEffect(() => {
    if (!token) return undefined;
    let active = true;
    setAddressesLoading(true);
    getCustomerAddresses()
      .then((response) => {
        if (active) setAddresses(response.addresses || []);
      })
      .catch((error) => {
        if (active) notify(getErrorMessage(error, "Could not load saved addresses."), "error");
      })
      .finally(() => {
        if (active) setAddressesLoading(false);
      });
    return () => {
      active = false;
    };
  }, [token, notify]);
  if (!isAuthenticated)
    return (
      <main className="page-shell content-page customer-profile-page">
        <div className="breadcrumbs"><Link to="/">Home</Link><span>/</span><span>Profile</span></div>
        <section className="profile-guest-card">
          <div className="profile-guest-icon"><UserRound size={30} /></div>
          <p className="eyebrow">YOUR ACCOUNT</p>
          <h1>Your Sugam Store profile</h1>
          <p>Sign in to see your member details, orders and wishlist in one place.</p>
          <button className="button button--primary" onClick={onLogin}>Sign in to your account <ArrowRight size={17} /></button>
        </section>
      </main>
    );
  const name = user?.name || "Sugam Store customer";
  const startEditing = () => {
    setForm({ name: user?.name || "", email: user?.email || "", gender: user?.gender || "" });
    setIsEditing(true);
  };
  const saveProfile = async (event) => {
    event.preventDefault();
    setIsSaving(true);
    try {
      const response = await updateCustomerProfile({
        name: form.name.trim(),
        email: form.email.trim(),
        gender: form.gender || null,
      });
      setSession(token, response.user);
      setIsEditing(false);
      notify("Your profile has been updated.");
    } catch (error) {
      notify(getErrorMessage(error, "Could not update your profile. Please try again."), "error");
    } finally {
      setIsSaving(false);
    }
  };
  const resetAddressForm = () => {
    setAddressForm(emptyAddress(user));
    setAddressEditingId("");
    setAddressFormOpen(false);
  };
  const openNewAddressForm = () => {
    setAddressForm(emptyAddress(user));
    setAddressEditingId("");
    setAddressFormOpen(true);
  };
  const openEditAddressForm = (address) => {
    setAddressForm({ ...emptyAddress(user), ...address });
    setAddressEditingId(address._id);
    setAddressFormOpen(true);
  };
  const updateAddressField = (event) => {
    const { name, value, type, checked } = event.target;
    setAddressForm((current) => ({
      ...current,
      [name]: type === "checkbox" ? checked : value,
    }));
  };
  const saveAddress = async (event) => {
    event.preventDefault();
    setAddressBusy(true);
    const addressData = {
      ...addressForm,
      fullName: addressForm.fullName.trim(),
      phoneNo: addressForm.phoneNo.trim(),
      address: addressForm.address.trim(),
      city: addressForm.city.trim(),
      state: addressForm.state.trim(),
      country: addressForm.country.trim(),
      pinCode: addressForm.pinCode.trim(),
      landmark: addressForm.landmark.trim(),
    };
    try {
      const response = addressEditingId
        ? await updateCustomerAddress(addressEditingId, addressData)
        : await addCustomerAddress(addressData);
      setAddresses(response.addresses || []);
      resetAddressForm();
      notify(addressEditingId ? "Address updated." : "Address saved.");
    } catch (error) {
      notify(getErrorMessage(error, "Could not save this address. Please try again."), "error");
    } finally {
      setAddressBusy(false);
    }
  };
  const removeAddress = async (addressId) => {
    if (!window.confirm("Delete this saved address?")) return;
    setAddressBusy(true);
    try {
      const response = await deleteCustomerAddress(addressId);
      setAddresses(response.addresses || []);
      if (addressEditingId === addressId) resetAddressForm();
      notify("Address deleted.");
    } catch (error) {
      notify(getErrorMessage(error, "Could not delete this address."), "error");
    } finally {
      setAddressBusy(false);
    }
  };
  const makeDefaultAddress = async (addressId) => {
    setAddressBusy(true);
    try {
      const response = await setDefaultCustomerAddress(addressId);
      setAddresses(response.addresses || []);
      notify("Default delivery address updated.");
    } catch (error) {
      notify(getErrorMessage(error, "Could not update the default address."), "error");
    } finally {
      setAddressBusy(false);
    }
  };
  const useCurrentLocation = async () => {
    if (!navigator.geolocation) {
      notify("Location is not available in this browser.", "error");
      return;
    }
    setLocationLoading(true);
    try {
      const position = await new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          enableHighAccuracy: true,
          timeout: 12000,
          maximumAge: 60000,
        });
      });
      const { latitude, longitude } = position.coords;
      const response = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=jsonv2&addressdetails=1&lat=${encodeURIComponent(latitude)}&lon=${encodeURIComponent(longitude)}`,
        { headers: { Accept: "application/json" } },
      );
      if (!response.ok) throw new Error("Could not look up this location. Please enter your address manually.");
      const result = await response.json();
      const location = result.address || {};
      const street = [location.house_number, location.road || location.residential || location.pedestrian]
        .filter(Boolean)
        .join(" ");
      setAddressForm((current) => ({
        ...current,
        address: street || location.neighbourhood || location.suburb || result.display_name?.split(",").slice(0, 2).join(",") || current.address,
        city: location.city || location.town || location.village || location.municipality || location.county || current.city,
        state: location.state || current.state,
        country: location.country || current.country,
        pinCode: location.postcode || current.pinCode,
      }));
      notify("Current location added. Please check the address before saving.");
    } catch (error) {
      if (typeof error?.code === "number") {
        const locationErrors = {
          1: "Location permission was denied. Allow location access and try again.",
          2: "Your current location could not be found. Try again or enter it manually.",
          3: "Location request timed out. Try again.",
        };
        notify(locationErrors[error.code] || "Could not get your current location.", "error");
      } else {
        notify(getErrorMessage(error, "Could not find the address for your location."), "error");
      }
    } finally {
      setLocationLoading(false);
    }
  };
  const uploadProfilePhoto = async (event) => {
    const imageFile = event.target.files?.[0];
    if (!imageFile) return;
    if (!imageFile.type.startsWith("image/")) {
      notify("Please choose an image file.", "error");
      event.target.value = "";
      return;
    }
    if (imageFile.size > 5 * 1024 * 1024) {
      notify("Choose an image smaller than 5 MB.", "error");
      event.target.value = "";
      return;
    }
    setIsUploadingPhoto(true);
    try {
      const response = await uploadCustomerProfileImage(imageFile);
      setSession(token, response.user);
      notify("Profile photo updated.");
    } catch (error) {
      notify(getErrorMessage(error, "Could not upload your profile photo. Please try again."), "error");
    } finally {
      setIsUploadingPhoto(false);
      event.target.value = "";
    }
  };
  const copyMemberId = async () => {
    if (!user?.memberId) return;
    try {
      await navigator.clipboard.writeText(user.memberId);
      notify("Member ID copied.");
    } catch {
      notify("Could not copy the Member ID. Please select and copy it.", "error");
    }
  };
  const shareReferralLink = async () => {
    if (!user?.memberId) {
      notify("Your Member ID is not available yet.", "error");
      return;
    }
    const referralUrl = new URL("/register", window.location.origin);
    referralUrl.searchParams.set("sponsorId", user.memberId);
    const shareData = {
      title: "Join Sugam Store",
      text: `Register with my Sugam Store referral link. Member ID: ${user.memberId}`,
      url: referralUrl.toString(),
    };
    try {
      if (navigator.share) {
        await navigator.share(shareData);
        return;
      }
      await navigator.clipboard.writeText(shareData.url);
      notify("Referral link copied.");
    } catch (error) {
      if (error?.name === "AbortError") return;
      try {
        await navigator.clipboard.writeText(shareData.url);
        notify("Referral link copied.");
      } catch {
        notify("Could not share the link. Please try again.", "error");
      }
    }
  };

  return (
    <main className="page-shell content-page customer-profile-page">
      <div className="breadcrumbs">
        <Link to="/">Home</Link>
        <span>/</span>
        <span>Profile</span>
      </div>
      <header className="profile-page-heading">
        <div>
          <p className="eyebrow">ACCOUNT CENTER</p>
          <h1>My Profile</h1>
          <p>Manage your details and keep track of your Sugam Store activity.</p>
        </div>
        <button
          className="button button--quiet profile-signout"
          onClick={() => {
            clearSession();
            notify("You’ve signed out.");
          }}
        >
          <LogOut size={17} /> Sign out
        </button>
      </header>

      <section className="profile-hero">
        <div className="profile-hero-main">
          <div className="profile-avatar-wrap">
            <div className="profile-hero-avatar">
              {getProfileImage(user) ? <img src={getProfileImage(user)} alt={`${name}'s profile`} /> : (name.trim()[0] || "S").toUpperCase()}
            </div>
            <button className="profile-photo-upload" type="button" onClick={() => photoInputRef.current?.click()} disabled={isUploadingPhoto} aria-label="Change profile photo" title={isUploadingPhoto ? "Uploading photo" : "Change profile photo"}>
              <Camera size={15} />
            </button>
            <input ref={photoInputRef} className="profile-photo-input" type="file" accept="image/jpeg,image/png,image/gif,image/webp" onChange={uploadProfilePhoto} aria-label="Choose a profile photo" />
          </div>
          <div className="profile-hero-copy">
            <span className="profile-member-badge"><BadgeCheck size={15} /> Sugam Store Member</span>
            <h2>{name}</h2>
            <p>{user?.email || "Add an email to your account"}</p>
          </div>
        </div>
        <div className="profile-member-id">
          <div>
            <span>YOUR MEMBER ID</span>
            <strong>{user?.memberId || "Not available"}</strong>
          </div>
          <div className="profile-member-actions">
            <button type="button" onClick={copyMemberId} disabled={!user?.memberId} aria-label="Copy member ID">
              <Copy size={16} /> Copy ID
            </button>
            <button type="button" onClick={shareReferralLink} disabled={!user?.memberId} aria-label="Share referral link">
              <Share2 size={16} /> Share
            </button>
          </div>
        </div>
      </section>

      <section className="profile-shortcuts" aria-label="Account shortcuts">
        <Link to="/orders">
          <span className="profile-shortcut-icon profile-shortcut-icon--orders"><PackageCheck size={21} /></span>
          <span className="profile-shortcut-copy">
            <small>SHOPPING ACTIVITY</small>
            <strong>My orders</strong>
            <span>View purchases and delivery updates</span>
          </span>
          <ArrowRight className="profile-shortcut-arrow" size={19} />
        </Link>
        <Link to="/wishlist">
          <span className="profile-shortcut-icon profile-shortcut-icon--wishlist"><Heart size={21} /></span>
          <span className="profile-shortcut-copy">
            <small>SAVED FOR LATER</small>
            <strong>My wishlist</strong>
            <span>Return to the things you love</span>
          </span>
          <ArrowRight className="profile-shortcut-arrow" size={19} />
        </Link>
      </section>

      <section className="profile-details-card">
        <div className="profile-section-heading profile-details-heading">
          <span className="profile-section-icon"><UserRound size={18} /></span>
          <div><p className="eyebrow">PERSONAL DETAILS</p><h2>Account information</h2></div>
          {!isEditing && <button className="button button--quiet profile-edit-button" type="button" onClick={startEditing}><Edit3 size={16} /> Edit profile</button>}
        </div>
        {isEditing ? (
          <form className="profile-edit-form" onSubmit={saveProfile}>
            <label className="field">
              Full name
              <input maxLength={30} required value={form.name} onChange={(event) => setForm((current) => ({ ...current, name: event.target.value }))} autoComplete="name" />
            </label>
            <label className="field">
              Email address
              <input type="email" value={form.email} onChange={(event) => setForm((current) => ({ ...current, email: event.target.value }))} autoComplete="email" />
            </label>
            <label className="field">
              Gender
              <select value={form.gender} onChange={(event) => setForm((current) => ({ ...current, gender: event.target.value }))}>
                <option value="">Select gender</option>
                <option value="male">Male</option>
                <option value="female">Female</option>
                <option value="other">Other</option>
                <option value="prefer_not_to_say">Prefer not to say</option>
              </select>
            </label>
            <p className="profile-edit-note">Your mobile number, member ID and sponsor ID are account identifiers and can’t be changed here.</p>
            <div className="profile-edit-actions">
              <button className="button button--quiet" type="button" onClick={() => setIsEditing(false)} disabled={isSaving}>Cancel</button>
              <button className="button button--primary" type="submit" disabled={isSaving}><Check size={16} /> {isSaving ? "Saving…" : "Save changes"}</button>
            </div>
          </form>
        ) : (
          <div className="profile-details-grid">
            <div className="profile-info-item"><span className="profile-info-icon"><UserRound size={17} /></span><div><small>Full name</small><strong>{name}</strong></div></div>
            <div className="profile-info-item"><span className="profile-info-icon"><Phone size={17} /></span><div><small>Mobile number</small><strong>{user?.phone || "Not added"}</strong></div></div>
            <div className="profile-info-item"><span className="profile-info-icon"><Mail size={17} /></span><div><small>Email address</small><strong>{user?.email || "Not added"}</strong></div></div>
            <div className="profile-info-item"><span className="profile-info-icon"><UserRound size={17} /></span><div><small>Gender</small><strong>{{ male: "Male", female: "Female", other: "Other", prefer_not_to_say: "Prefer not to say" }[user?.gender] || "Not specified"}</strong></div></div>
            <div className="profile-info-item"><span className="profile-info-icon"><ShieldCheck size={17} /></span><div><small>Sponsor ID</small><strong>{user?.sponsorId || "No sponsor linked"}</strong></div></div>
          </div>
        )}
      </section>

      <section className="profile-addresses-card">
        <div className="profile-addresses-heading">
          <div className="profile-section-heading">
            <span className="profile-section-icon"><MapPin size={18} /></span>
            <div>
              <p className="eyebrow">DELIVERY DETAILS</p>
              <h2>Saved addresses <span className="profile-address-count">{addresses.length}</span></h2>
            </div>
          </div>
          {!addressFormOpen && (
            <button className="button button--primary profile-add-address" type="button" onClick={openNewAddressForm}>
              <Plus size={17} /> Add address
            </button>
          )}
        </div>

        {addressFormOpen && (
          <form className="profile-address-form" onSubmit={saveAddress}>
            <div className="profile-address-form-top">
              <h3>{addressEditingId ? "Edit address" : "Add a delivery address"}</h3>
              <button className="profile-location-button" type="button" onClick={useCurrentLocation} disabled={locationLoading || addressBusy}>
                <LocateFixed size={16} /> {locationLoading ? "Finding location…" : "Use current location"}
              </button>
            </div>
            <p className="profile-address-hint">We’ll fill in the address details from your location. Please check them before saving.</p>
            <div className="profile-address-fields">
              <label className="field">Full name<input name="fullName" value={addressForm.fullName} onChange={updateAddressField} autoComplete="name" required /></label>
              <label className="field">Phone number<input name="phoneNo" type="tel" value={addressForm.phoneNo} onChange={updateAddressField} autoComplete="tel" required /></label>
              <label className="field profile-address-field-wide">House, street and area<input name="address" value={addressForm.address} onChange={updateAddressField} autoComplete="street-address" required /></label>
              <label className="field">City<input name="city" value={addressForm.city} onChange={updateAddressField} autoComplete="address-level2" required /></label>
              <label className="field">State<input name="state" value={addressForm.state} onChange={updateAddressField} autoComplete="address-level1" required /></label>
              <label className="field">PIN code<input name="pinCode" inputMode="numeric" value={addressForm.pinCode} onChange={updateAddressField} autoComplete="postal-code" required /></label>
              <label className="field">Country<input name="country" value={addressForm.country} onChange={updateAddressField} autoComplete="country-name" required /></label>
              <label className="field">Landmark (optional)<input name="landmark" value={addressForm.landmark} onChange={updateAddressField} /></label>
              <label className="field">Address type<select name="label" value={addressForm.label} onChange={updateAddressField}><option value="home">Home</option><option value="work">Work</option><option value="other">Other</option></select></label>
            </div>
            <label className="profile-default-checkbox"><input type="checkbox" name="isDefault" checked={Boolean(addressForm.isDefault)} onChange={updateAddressField} /> Make this my default delivery address</label>
            <div className="profile-address-form-actions">
              <button className="button button--quiet" type="button" onClick={resetAddressForm} disabled={addressBusy}>Cancel</button>
              <button className="button button--primary" type="submit" disabled={addressBusy}>{addressBusy ? "Saving…" : addressEditingId ? "Save address" : "Add address"}</button>
            </div>
          </form>
        )}

        {addressesLoading ? (
          <p className="profile-address-empty">Loading your saved addresses…</p>
        ) : addresses.length ? (
          <div className="profile-address-list">
            {addresses.map((address) => {
              const label = address.label || "home";
              const AddressIcon = label === "work" ? BriefcaseBusiness : label === "home" ? Home : MapPin;
              return (
                <article className="profile-address-item" key={address._id}>
                  <div className="profile-address-item-heading">
                    <span className={`profile-address-type profile-address-type--${label}`}><AddressIcon size={16} /> {label}</span>
                    {address.isDefault && <span className="profile-default-badge"><Star size={13} /> Default</span>}
                  </div>
                  <strong>{address.fullName}</strong>
                  <p>{address.address}{address.landmark ? `, ${address.landmark}` : ""}</p>
                  <p>{[address.city, address.state, address.pinCode, address.country].filter(Boolean).join(", ")}</p>
                  <small>{address.phoneNo}</small>
                  <div className="profile-address-item-actions">
                    {!address.isDefault && <button type="button" onClick={() => makeDefaultAddress(address._id)} disabled={addressBusy}><Star size={15} /> Set default</button>}
                    <button type="button" onClick={() => openEditAddressForm(address)} disabled={addressBusy}><Edit3 size={15} /> Edit</button>
                    <button className="profile-address-delete" type="button" onClick={() => removeAddress(address._id)} disabled={addressBusy}><Trash2 size={15} /> Delete</button>
                  </div>
                </article>
              );
            })}
          </div>
        ) : !addressFormOpen ? (
          <div className="profile-address-empty">
            <MapPin size={23} />
            <strong>No saved addresses yet</strong>
            <span>Add more than one address to make checkout quicker.</span>
          </div>
        ) : null}
      </section>
    </main>
  );
}
